import { differenceInYears, Duration, format, isValid, parse } from 'date-fns';

export const formatDateString = (dateString: string): string | undefined => {
    try {
        const parsedDate = parse(dateString, 'yyyyMMdd', new Date());
        if (!isValid(parsedDate)) {
            return undefined;
        }
        return formatSeriesDate(parsedDate);
    } catch (error) {
        return undefined;
    }
};

export function formatDate(dateString: string) {
    try {
        if (dateString) {
            return parse(dateString, 'yyyyMMdd', new Date());
        } else {
            return undefined;
        }
    } catch (error) {
        return undefined;
    }
}

/**
 * Formats a date-fns Duration object into a time string (mm:ss or HH:mm:ss).
 * @param duration - The Duration object from date-fns.
 * @returns A formatted time string.
 */
export const formatDurationTime = (duration: Duration): string => {
    const date = new Date(0);
    date.setHours(duration.hours || 0);
    date.setMinutes(duration.minutes || 0);
    date.setSeconds(duration.seconds || 0);

    const formatString = (duration.hours || 0) > 0 ? 'HH:mm:ss' : 'mm:ss';
    return format(date, formatString);
};

/**
 * Formats a Date object for display (e.g., "Dec 3 2025").
 * @param date - The Date object to format.
 * @returns A formatted date string, or empty string if date is undefined.
 */
export const formatSeriesDate = (date: Date | undefined): string => {
    if (!date) {
        return '';
    }
    const locale = navigator.language;
    return format(date, locale === 'en-US' ? 'MMM d yyyy' : 'd MMM yyyy');
};

/**
 * Formats a DICOM date string (yyyyMMdd) for display (e.g., "Jan 1 1900").
 * @param dateString - The DICOM date string in yyyyMMdd format.
 * @returns A formatted date string, or undefined if parsing fails.
 */
export const formatDateCompact = (dateString: string): string | undefined => {
    const parsedDate = parse(dateString, 'yyyyMMdd', new Date());
    if (!isValid(parsedDate)) {
        return undefined;
    }
    return formatSeriesDate(parsedDate);
};

/**
 * Calculates age in years from a DICOM date string (yyyyMMdd).
 * @param dateString - The DICOM date string in yyyyMMdd format.
 * @returns Age in years, or undefined if parsing fails or date is in the future.
 */
export const calculateAge = (dateString: string): number | undefined => {
    const parsedDate = parse(dateString, 'yyyyMMdd', new Date());
    if (!isValid(parsedDate)) {
        return undefined;
    }
    const age = differenceInYears(new Date(), parsedDate);
    if (age < 0) {
        return undefined;
    }
    return age;
};
